/**
 * Página de Redefinição de Senha do Sistema MEDICARE
 * Permite ao usuário redefinir sua senha usando um token
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Heading,
  Link,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  IconButton,
  useColorModeValue,
  Flex,
  Card,
  CardBody,
  Divider,
  Icon,
  Image,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useNotifications } from '../../contexts/ToastContext';
import { UsuariosService } from '../../services/usuarios';

interface ResetPasswordFormData {
  senha: string;
  confirmarSenha: string;
}

export const ResetPasswordPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>();

  const senha = watch('senha');

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError('Token de redefinição inválido');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await UsuariosService.redefinirSenha(token, data.senha);
      setIsSuccess(true);
      showSuccess('Senha redefinida com sucesso!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.mensagem || 'Erro ao redefinir senha';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={bgColor}
        px={4}
      >
        <Container maxW="md">
          <Card bg={cardBg} shadow="xl" borderRadius="xl">
            <CardBody p={8}>
              <VStack spacing={6}>
                {/* Ícone de sucesso */}
                <Box
                  w={16}
                  h={16}
                  bg="green.500"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiCheck} color="white" boxSize={8} />
                </Box>

                <VStack spacing={4} textAlign="center">
                  <Heading size="lg" color="green.500">
                    Senha Redefinida!
                  </Heading>
                  <Text color={textColor}>
                    Sua senha foi redefinida com sucesso.
                    Agora você pode fazer login com sua nova senha.
                  </Text>
                </VStack>

                <Divider />

                <Button
                  colorScheme="medicare"
                  size="lg"
                  w="full"
                  onClick={() => navigate('/login')}
                >
                  Fazer Login
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Flex>
    );
  }

  if (!token) {
    return (
      <Flex
        minH="100vh"
        align="center"
        justify="center"
        bg={bgColor}
        px={4}
      >
        <Container maxW="md">
          <Card bg={cardBg} shadow="xl" borderRadius="xl">
            <CardBody p={8}>
              <VStack spacing={6}>
                <Alert status="error">
                  <AlertIcon />
                  Token de redefinição inválido ou expirado.
                </Alert>
                <Link
                  as={RouterLink}
                  to="/esqueci-senha"
                  color="medicare.500"
                  fontWeight="medium"
                >
                  Solicitar novo link de recuperação
                </Link>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Flex>
    );
  }

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={bgColor}
      px={4}
    >
      <Container maxW="md">
        <Card bg={cardBg} shadow="xl" borderRadius="xl">
          <CardBody p={8}>
            <VStack spacing={6}>
              {/* Logo e título */}
              <VStack spacing={4}>
                <Box
                  w={64}
                  h={64}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src="/logo_medicare.png"
                    alt="Logo Medicare"
                    w={64}
                    h={64}
                    objectFit="contain"
                  />
                </Box>
                <VStack spacing={2}>
                  <Heading
                    size="lg"
                    color="medicare.500"
                    textAlign="center"
                  >
                    Nova Senha
                  </Heading>
                  <Text
                    color={textColor}
                    textAlign="center"
                  >
                    Digite sua nova senha
                  </Text>
                </VStack>
              </VStack>

              <Divider />

              {/* Formulário */}
              <Box w="full">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <VStack spacing={4}>
                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <FormControl isInvalid={!!errors.senha}>
                      <FormLabel>Nova Senha</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Digite sua nova senha"
                          {...register('senha', {
                            required: 'Senha é obrigatória',
                            minLength: {
                              value: 8,
                              message: 'Senha deve ter pelo menos 8 caracteres',
                            },
                            pattern: {
                              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                              message: 'Senha deve conter ao menos: 1 letra minúscula, 1 maiúscula, 1 número e 1 caractere especial',
                            },
                          })}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            icon={showPassword ? <FiEyeOff /> : <FiEye />}
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      {errors.senha && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.senha.message}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={!!errors.confirmarSenha}>
                      <FormLabel>Confirmar Nova Senha</FormLabel>
                      <InputGroup>
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirme sua nova senha"
                          {...register('confirmarSenha', {
                            required: 'Confirmação de senha é obrigatória',
                            validate: (value) =>
                              value === senha || 'As senhas não coincidem',
                          })}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      {errors.confirmarSenha && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.confirmarSenha.message}
                        </Text>
                      )}
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="medicare"
                      size="lg"
                      w="full"
                      isLoading={isLoading}
                      loadingText="Redefinindo..."
                    >
                      Redefinir Senha
                    </Button>
                  </VStack>
                </form>
              </Box>

              {/* Link para voltar */}
              <Link
                as={RouterLink}
                to="/login"
                color="medicare.500"
                fontSize="sm"
                fontWeight="medium"
              >
                <Icon as={FiArrowLeft} mr={2} />
                Voltar ao login
              </Link>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Flex>
  );
};

export default ResetPasswordPage;